import { Angry, Laugh, Meh, Smile } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FeedbackRating } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { usePostFeedback } from "../api/post-feedback";
import {
  type CallFeedbackFormData,
  callFeedbackSchema,
} from "../types/call-feedback.schema";

const defaultValues: CallFeedbackFormData = {
  rating: null,
  description: "",
  isUserHappyToBeContacted: false,
};

const FeedBackRatingsToIcons: Record<FeedbackRating, React.ReactNode> = {
  Bad: <Angry />,
  Ok: <Meh />,
  Good: <Smile />,
  Great: <Laugh />,
};

type CallFeedbackFormProps = {
  setIsSubmitted: (isSubmitted: boolean) => void;
  isSubmitted: boolean;
};

export function CallFeedbackForm({
  setIsSubmitted,
  isSubmitted,
}: CallFeedbackFormProps) {
  const form = useForm<CallFeedbackFormData>({
    resolver: zodResolver(callFeedbackSchema),
    defaultValues,
  });

  const { mutateAsync: postFeedback, isPending } = usePostFeedback();

  const handleSubmit = async (data: CallFeedbackFormData) => {
    await postFeedback(data);
    setIsSubmitted(true);
    form.reset();

    setTimeout(() => {
      setIsSubmitted(false);
    }, 3000);
  };

  const rating = form.watch("rating");

  const isSubmitDisabled =
    isPending || !form.formState.isValid || rating === null;

  if (isSubmitted) {
    return (
      <div className="flex flex-col gap-6">
        <p className="text-center text-white">Thank you for your feedback!</p>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-6"
      >
        <FormField
          control={form.control}
          name="rating"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                How would you rate your experience using ClassView?
              </FormLabel>
              <FormControl>
                <ToggleGroup
                  type="single"
                  onValueChange={field.onChange}
                  value={field.value?.toString()}
                  className="relative flex h-[60px] gap-x-0 divide-x divide-border rounded-md border border-border shadow-sm"
                >
                  {Object.keys(FeedBackRatingsToIcons).map((rating) => (
                    <ToggleGroupItem
                      key={rating}
                      value={rating}
                      variant="ghost"
                      className="flex h-full flex-col items-center gap-[2px] py-2"
                    >
                      {FeedBackRatingsToIcons[rating as FeedbackRating]}
                      <span className="font-normal">{rating}</span>
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Why have you given this rating?</FormLabel>
              <FormControl>
                <Textarea placeholder="Enter a description..." {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="isUserHappyToBeContacted"
          render={({ field }) => (
            <FormItem>
              <FormControl className="flex gap-1.5">
                <div>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <span className="text-sm text-white">
                    I am happy to be contacted and provide additional feedback
                  </span>
                </div>
              </FormControl>
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitDisabled}
          isLoading={isPending}
        >
          {isPending ? "Sending..." : "Send Feedback"}
        </Button>
      </form>
    </Form>
  );
}
